import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  buildCashTracker,
  CashTrackerSummary,
} from '../../api/cashTrackerApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload your Input.xlsx file containing the required sheets.',
  "The tracker is generated for today's date automatically (override it with the as-of date below if needed).",
  'Download the output file with 3 tracker sheets added.',
];

const FEATURES = [
  '30-day rolling cash position',
  'Full current-month tracker',
  'Next-month forecast',
  'Overdue payment highlighting',
  'Colour-coded receipts, payments and balances',
];

const REQUIRED_SHEETS: { sheet: string; reads: string }[] = [
  { sheet: 'Bank Balance', reads: 'Row 3: Kotak, SBI, SBI CC balances' },
  { sheet: 'Receivables Tracker', reads: 'Column J = Expected date of receipt' },
  {
    sheet: 'Payable Tracker',
    reads: 'Column M = status ("Not Paid" / "Paid"); overdue rolled to today',
  },
  { sheet: 'Fixed Cost Projections', reads: 'Column D = Expected payment date' },
  { sheet: 'Fixed Cost Structure Master', reads: 'Preserved in output (not processed)' },
];

const COLOUR_LEGEND: { label: string; className: string }[] = [
  { label: 'Today', className: 'bg-yellow-400' },
  { label: 'Overdue', className: 'bg-orange-500' },
  { label: 'Receipts / positive', className: 'bg-emerald-500' },
  { label: 'Payments / negative', className: 'bg-rose-500' },
  { label: 'Weekends', className: 'bg-violet-300' },
];

interface CashVaultPanelProps {
  mission: Mission;
  onBack: () => void;
}

const currency = (value: number | undefined) =>
  value == null
    ? '—'
    : value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

function CashVaultPanel({ mission, onBack }: CashVaultPanelProps) {
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [asOfDate, setAsOfDate] = useState('');
  const [outputFilename, setOutputFilename] = useState('Daily_Cash_Tracker.xlsx');
  const { status, error, result, fail, run } = useFileRun<FileResult<CashTrackerSummary>>();

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showFormat, setShowFormat] = useState(false);

  const handleSubmit = async () => {
    if (!inputFile) {
      fail('Upload the master workbook before building the cash tracker.');
      return;
    }
    await run(() => buildCashTracker(inputFile, asOfDate, outputFilename), 'Build failed.');
  };

  const summary = result?.summary ?? null;

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse
        steps={STEPS}
        footer={
          <>
            <p className="mb-2 font-semibold text-foreground">✨ Supported features</p>
            <ul className="space-y-1.5">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <span className="text-green-600">✅</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </>
        }
      />

      <div className="mb-5">
        <Button
          variant="ghost"
          onClick={() => setShowFormat((s) => !s)}
          className="h-auto gap-1.5 p-0 text-sm font-normal text-foreground hover:bg-transparent"
        >
          <span
            className={`inline-block transition-transform duration-150 ${showFormat ? 'rotate-90' : ''}`}
          >
            ›
          </span>
          📝 Expected file format — required sheets in Input.xlsx
        </Button>
        {showFormat && (
          <div className="mt-2 rounded-lg border border-border bg-muted p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="py-1.5 pr-3 font-semibold">Sheet</th>
                    <th className="py-1.5 pl-3 font-semibold">What is read</th>
                  </tr>
                </thead>
                <tbody>
                  {REQUIRED_SHEETS.map(({ sheet, reads }) => (
                    <tr key={sheet} className="border-b border-border/50 align-top text-foreground">
                      <td className="py-1.5 pr-3 font-medium">{sheet}</td>
                      <td className="py-1.5 pl-3 text-muted-foreground">{reads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Colour legend:</span>
              {COLOUR_LEGEND.map(({ label, className }) => (
                <span key={label} className="flex items-center gap-1.5">
                  <span className={`inline-block size-2.5 rounded-full ${className}`} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">Master workbook (required)*</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setInputFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={dragActive ? { borderColor: mission.color } : undefined}
        >
          <Button
            onClick={() => inputRef.current?.click()}
            style={{ backgroundColor: mission.color }}
            className="text-white hover:opacity-90"
          >
            <ArrowUpIcon /> Upload workbook
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">{mission.maxSizeLabel}</span>
          <input
            ref={inputRef}
            type="file"
            accept={mission.accept}
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setInputFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {inputFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {inputFile.name}</span>
            <button
              type="button"
              onClick={() => setInputFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove workbook"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="as-of-date" className="mb-1.5">
          As-of date (optional)
        </Label>
        <Input
          id="as-of-date"
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Leave blank to use the server&rsquo;s current date.
        </p>
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="Daily_Cash_Tracker.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building…' : 'Build Cash Tracker'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          ✅ {result.filename} downloaded.
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload a master workbook above to get started.
        </div>
      )}

      {summary && (
        <div className="mt-5 rounded-lg border border-border bg-muted p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              📊 Cash summary — as of {summary.as_of}
            </p>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                summary.overdue ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}
            >
              {summary.overdue ? 'Overdue items' : 'No overdue'}
            </span>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Forecast range</dt>
            <dd className="text-right font-medium text-foreground">
              {summary.range_start} → {summary.range_end}
            </dd>
            <dt className="text-muted-foreground">Bank balance</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.bank)}</dd>
            <dt className="text-muted-foreground">Total receivables</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_rec)}</dd>
            <dt className="text-muted-foreground">Total payables</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_pay)}</dd>
            <dt className="text-muted-foreground">Projected closing</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.closing)}</dd>
          </dl>

          {summary.bank_detail && Object.keys(summary.bank_detail).length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Bank detail
              </p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {Object.entries(summary.bank_detail).map(([name, value]) => (
                  <div key={name} className="contents">
                    <dt className="text-muted-foreground">{name}</dt>
                    <dd className="text-right font-medium text-foreground">{currency(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {summary.sheets && summary.sheets.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Sheets generated
              </p>
              <div className="flex flex-wrap gap-2">
                {summary.sheets.map((sheet) => (
                  <span
                    key={sheet}
                    className="rounded-full bg-background px-2.5 py-0.5 text-xs font-medium text-foreground"
                  >
                    {sheet}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default CashVaultPanel;
